<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      ref="panelRef"
      class="power-insufficient-dialog fixed inset-0 z-[30000] flex items-center justify-center bg-black/50"
      @click.self="handleDismiss"
    >
      <div
        class="bg-card border border-border rounded-[10px] shadow-[0_10px_40px_rgba(0,0,0,0.2)] w-full max-w-[420px] mx-4 overflow-hidden"
      >
        <div class="px-6 py-4 bg-muted/20 border-b border-border">
          <p class="text-[16px] font-semibold text-foreground">{{ t("powerInsufficientTitle") }}</p>
        </div>
        <div class="px-6 py-5">
          <p class="text-[14px] leading-[1.7] text-muted-foreground">{{ t("powerInsufficientBody") }}</p>
        </div>
        <div class="px-6 pb-5 flex items-center justify-end gap-3">
          <button
            type="button"
            class="h-9 px-4 rounded-[6px] border border-border text-[13px] text-foreground hover:bg-muted transition-colors"
            @click="handleDismiss"
          >
            {{ t("powerInsufficientLater") }}
          </button>
          <button
            type="button"
            class="power-insufficient-dialog__confirm h-9 px-4 rounded-[6px] text-[13px] hover:opacity-90 transition-opacity"
            @click="handleRecharge"
          >
            {{ t("planBuyBeans") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from "vue";
import { useI18n } from "@/i18n/useI18n";

/**
 * 梦想豆余额不足提示弹窗。
 * 展示与跳转由同目录 {@link ensurePowerCost} 控制；本文件只负责 UI。
 */

const { t } = useI18n();

/** 遮罩根节点：Teleport 到 body 后在此继承页面主题变量 */
const panelRef = ref<HTMLElement | null>(null);

/**
 * 弹窗挂到 body，读不到创作页对 `--primary` 的覆盖，按钮会落到旧紫色。
 * 把页面上已声明的主题变量抄到弹窗根节点，按钮继续使用 `var(--primary)`。
 */
const syncThemePrimary = () => {
  const panel = panelRef.value;
  const source = document.querySelector(".ai-creation-theme");
  if (!panel || !(source instanceof HTMLElement)) {
    return;
  }
  const style = getComputedStyle(source);
  const primary = style.getPropertyValue("--primary").trim();
  const foreground = style.getPropertyValue("--primary-foreground").trim();
  if (primary) {
    panel.style.setProperty("--primary", primary);
  }
  if (foreground) {
    panel.style.setProperty("--primary-foreground", foreground);
  }
};

onMounted(() => {
  void nextTick(syncThemePrimary);
});

defineProps<{
  /** 是否展示弹窗 */
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  /** 点击立即充值 */
  (e: "recharge"): void;
  /** 点击暂不处理或关闭 */
  (e: "dismiss"): void;
}>();

/** 关闭弹窗且不跳转充值 */
const handleDismiss = () => {
  emit("update:modelValue", false);
  emit("dismiss");
};

/** 关闭弹窗并跳转会员中心充值页 */
const handleRecharge = () => {
  emit("update:modelValue", false);
  emit("recharge");
};
</script>

<style>
/* 充值按钮使用统一主题色，不写死色值；Teleport 后由脚本把页面 --primary 挂到遮罩上 */
.power-insufficient-dialog__confirm {
  background-color: var(--primary);
  color: var(--primary-foreground);
}
</style>
